import React, { useState, useEffect } from "react";
import useAxios from "../../utils/useAxios";
import {
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaSearch,
  FaFilter,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaSpinner,
  FaGoogle,
  FaIdCard,
  FaBirthdayCake,
  FaVenusMars,
  FaThLarge,
  FaList,
  FaGlobe,
  FaMousePointer,
  FaExternalLinkAlt,
  FaUserCheck,
  FaCamera,
} from "react-icons/fa";
import { showModal } from "../../utils/modalUtility";

const customStyles = `
  .user-personal-card {
    border: none;
    border-radius: 18px;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
    transition: all 0.25s ease-in-out;
    overflow: hidden;
    position: relative;
  }
  .user-personal-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 32px rgba(15, 23, 42, 0.12);
  }
  .avatar-container {
    position: relative;
    width: 90px;
    height: 90px;
    margin: 0 auto 14px auto;
  }
  .avatar-img {
    width: 90px;
    height: 90px;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
    border: 3.5px solid #ffffff;
  }
  .avatar-fallback {
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.2rem;
    font-weight: 700;
    box-shadow: 0 4px 14px rgba(99, 102, 241, 0.25);
    border: 3.5px solid #ffffff;
    text-transform: uppercase;
  }
  .verification-dot {
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.72rem;
    color: #ffffff;
    border: 2px solid #ffffff;
  }
  .stat-card-gradient-1 {
    background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
    color: #ffffff;
    border-radius: 16px;
  }
  .stat-card-gradient-2 {
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    color: #ffffff;
    border-radius: 16px;
  }
  .stat-card-gradient-3 {
    background: linear-gradient(135deg, #0284c7 0%, #38bdf8 100%);
    color: #ffffff;
    border-radius: 16px;
  }
  .stat-card-gradient-4 {
    background: linear-gradient(135deg, #ea580c 0%, #f97316 100%);
    color: #ffffff;
    border-radius: 16px;
  }
  .page-header-banner {
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 16px;
    padding: 24px;
    border: 1px solid #e2e8f0;
  }
  .badge-pill-soft {
    border-radius: 50rem;
    padding: 0.35rem 0.75rem;
    font-weight: 600;
    font-size: 0.75rem;
  }
  .info-table td, .info-table th {
    padding: 10px 14px;
    vertical-align: middle;
  }
  .filter-input {
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    padding: 8px 14px;
  }
`;

const UserPersonalInfo = () => {
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "table"
  const [selectedUser, setSelectedUser] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [selectedGender, setSelectedGender] = useState("All");
  const [selectedCity, setSelectedCity] = useState("All");
  const [selectedRole, setSelectedRole] = useState("All");
  const [selectedVerified, setSelectedVerified] = useState("All");

  const axiosAPI = useAxios();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (selectedGender !== "All") params.append("gender", selectedGender);
      if (selectedCity !== "All") params.append("city", selectedCity);
      if (selectedRole !== "All") params.append("role", selectedRole);
      if (selectedVerified !== "All") params.append("verified", selectedVerified);

      const res = await axiosAPI.get(`/api/dashboard/user-personal-info?${params.toString()}`);
      if (res.data && res.data.success) {
        setUsers(res.data.data || []);
        setSummary(res.data.summary || {});
        setCities(res.data.cities || []);
      }
    } catch (err) {
      console.error("Error fetching user personal info:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [selectedGender, selectedCity, selectedRole, selectedVerified]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleResetFilters = () => {
    setSearch("");
    setSelectedGender("All");
    setSelectedCity("All");
    setSelectedRole("All");
    setSelectedVerified("All");
  };

  const handleOpenDetailModal = (user) => {
    setSelectedUser(user);
    showModal("userPersonalDetailModal");
  };

  const getAvatarUrl = (avatar) => {
    if (!avatar) return null;
    if (avatar.startsWith("http://") || avatar.startsWith("https://")) {
      return avatar;
    }
    const cleanPath = avatar.startsWith("/") ? avatar : `/${avatar}`;
    return `https://noonionnogarlic.com${cleanPath}`;
  };

  const getUserInitials = (user) => {
    const name = user.full_name || user.name || user.email || "U";
    return name.trim().charAt(0).toUpperCase();
  };

  return (
    <div className="container py-4">
      <style>{customStyles}</style>
      <div className="page-inner">

        {/* Page Header */}
        <div className="page-header-banner d-flex flex-wrap justify-content-between align-items-center shadow-sm mb-4 gap-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="badge bg-primary text-white rounded-pill px-3 py-1.5 fw-semibold" style={{ fontSize: "0.75rem" }}>
                Directory
              </span>
              <h3 className="fw-bold mb-0" style={{ color: "#0f172a" }}>
                User Personal Info
              </h3>
            </div>
            <p className="text-muted mb-0 small">
              View and manage complete personal profiles, profile photos, contact details, and engagement of all registered users.
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            {/* View Mode Toggle */}
            <div className="btn-group shadow-sm bg-white p-1 rounded-3 border">
              <button
                type="button"
                className={`btn btn-sm ${viewMode === "grid" ? "btn-primary text-white" : "btn-light text-secondary"}`}
                onClick={() => setViewMode("grid")}
                title="Card Grid View"
              >
                <FaThLarge className="me-1" /> Cards
              </button>
              <button
                type="button"
                className={`btn btn-sm ${viewMode === "table" ? "btn-primary text-white" : "btn-light text-secondary"}`}
                onClick={() => setViewMode("table")}
                title="Table View"
              >
                <FaList className="me-1" /> Table
              </button>
            </div>

            <button
              className="btn btn-outline-secondary btn-sm rounded-3 shadow-sm"
              onClick={fetchUsers}
              title="Refresh Directory"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Top Summary Stats Cards */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-sm-6 col-xl-3">
            <div className="stat-card-gradient-1 p-3 shadow-sm h-100 d-flex justify-content-between align-items-center">
              <div>
                <small className="text-white-50 text-uppercase fw-semibold" style={{ fontSize: "0.72rem" }}>
                  Total Registered Users
                </small>
                <h2 className="fw-bold mb-0 mt-1">{summary.total_users || 0}</h2>
                <small className="text-white-50" style={{ fontSize: "0.72rem" }}>
                  {summary.male_count || 0} Male • {summary.female_count || 0} Female
                </small>
              </div>
              <div className="bg-white bg-opacity-20 rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
                <FaUser className="fs-4 text-white" />
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3">
            <div className="stat-card-gradient-2 p-3 shadow-sm h-100 d-flex justify-content-between align-items-center">
              <div>
                <small className="text-white-50 text-uppercase fw-semibold" style={{ fontSize: "0.72rem" }}>
                  Phone Verified Users
                </small>
                <h2 className="fw-bold mb-0 mt-1">{summary.phone_verified_count || 0}</h2>
                <small className="text-white-50" style={{ fontSize: "0.72rem" }}>
                  {summary.with_phone || 0} have phone registered
                </small>
              </div>
              <div className="bg-white bg-opacity-20 rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
                <FaPhoneAlt className="fs-4 text-white" />
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3">
            <div className="stat-card-gradient-3 p-3 shadow-sm h-100 d-flex justify-content-between align-items-center">
              <div>
                <small className="text-white-50 text-uppercase fw-semibold" style={{ fontSize: "0.72rem" }}>
                  Google Linked Accounts
                </small>
                <h2 className="fw-bold mb-0 mt-1">{summary.google_users || 0}</h2>
                <small className="text-white-50" style={{ fontSize: "0.72rem" }}>
                  Google OAuth Logins
                </small>
              </div>
              <div className="bg-white bg-opacity-20 rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
                <FaGoogle className="fs-4 text-white" />
              </div>
            </div>
          </div>

          <div className="col-12 col-sm-6 col-xl-3">
            <div className="stat-card-gradient-4 p-3 shadow-sm h-100 d-flex justify-content-between align-items-center">
              <div>
                <small className="text-white-50 text-uppercase fw-semibold" style={{ fontSize: "0.72rem" }}>
                  Users With Profile Photo
                </small>
                <h2 className="fw-bold mb-0 mt-1">{summary.with_avatar || 0}</h2>
                <small className="text-white-50" style={{ fontSize: "0.72rem" }}>
                  Custom or Google avatar
                </small>
              </div>
              <div className="bg-white bg-opacity-20 rounded-circle p-3 d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
                <FaCamera className="fs-4 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Filter Controls Card */}
        <div className="card shadow-sm border-0 mb-4 rounded-4">
          <div className="card-body p-3 p-md-4">
            <form onSubmit={handleSearchSubmit} className="row g-3 align-items-end">
              {/* Search Field */}
              <div className="col-12 col-md-4">
                <label className="form-label small fw-bold text-dark mb-1">
                  <FaSearch className="text-primary me-1" /> Search Directory
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control form-control-sm filter-input"
                    placeholder="Search name, phone, email, ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <button type="submit" className="btn btn-primary btn-sm px-3 rounded-end">
                    Search
                  </button>
                </div>
              </div>

              {/* Gender Filter */}
              <div className="col-6 col-md-2">
                <label className="form-label small fw-bold text-dark mb-1">
                  <FaVenusMars className="text-secondary me-1" /> Gender
                </label>
                <select
                  className="form-select form-select-sm filter-input"
                  value={selectedGender}
                  onChange={(e) => setSelectedGender(e.target.value)}
                >
                  <option value="All">All Genders</option>
                  <option value="m">Male</option>
                  <option value="f">Female</option>
                </select>
              </div>

              {/* City Filter */}
              <div className="col-6 col-md-2">
                <label className="form-label small fw-bold text-dark mb-1">
                  <FaMapMarkerAlt className="text-danger me-1" /> City
                </label>
                <select
                  className="form-select form-select-sm filter-input"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                >
                  <option value="All">All Cities</option>
                  {cities.map((c, i) => (
                    <option key={i} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {/* Verified Filter */}
              <div className="col-6 col-md-2">
                <label className="form-label small fw-bold text-dark mb-1">
                  <FaCheckCircle className="text-success me-1" /> Verification
                </label>
                <select
                  className="form-select form-select-sm filter-input"
                  value={selectedVerified}
                  onChange={(e) => setSelectedVerified(e.target.value)}
                >
                  <option value="All">All Accounts</option>
                  <option value="phone">Phone Verified</option>
                  <option value="email">Email Verified</option>
                  <option value="google">Google Connected</option>
                </select>
              </div>

              {/* Reset Action */}
              <div className="col-6 col-md-2 text-md-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm w-100"
                  onClick={handleResetFilters}
                >
                  Reset Filters
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Directory Results */}
        {loading ? (
          <div className="text-center my-5 py-5">
            <FaSpinner className="spinner fs-1 text-primary mb-3" />
            <h5 className="text-muted">Loading user personal info...</h5>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center my-5 py-5 bg-white rounded-4 shadow-sm border border-light">
            <FaUser className="fs-1 text-muted mb-3 opacity-50" />
            <h4 className="text-muted fw-bold">No Users Found</h4>
            <p className="text-muted small">No user records matched your search query or filter selection.</p>
            <button className="btn btn-sm btn-primary mt-2" onClick={handleResetFilters}>
              Clear Filters
            </button>
          </div>
        ) : viewMode === "grid" ? (
          /* =======================================
             CARD GRID VIEW (Focuses on Photo & Info)
             ======================================= */
          <div className="row g-4">
            {users.map((u) => {
              const avatarSrc = getAvatarUrl(u.avatar);
              const initials = getUserInitials(u);
              const totalClicks =
                (u.restaurant_clicks_count || 0) +
                (u.product_clicks_count || 0) +
                (u.recipe_clicks_count || 0) +
                (u.video_clicks_count || 0);

              return (
                <div className="col-12 col-sm-6 col-lg-4 col-xl-3" key={u.id}>
                  <div className="user-personal-card h-100 p-4 d-flex flex-column text-center">
                    {/* User ID and Role Pills */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="badge bg-light text-secondary border small px-2 py-1">
                        ID: #{u.id}
                      </span>
                      <span
                        className={`badge ${
                          u.role === "admin" || u.role === "super_admin"
                            ? "bg-danger"
                            : "bg-primary"
                        } text-white rounded-pill px-2.5 py-1 text-capitalize`}
                        style={{ fontSize: "0.7rem" }}
                      >
                        {u.role || "User"}
                      </span>
                    </div>

                    {/* Avatar / Photo with Verification Badge */}
                    <div className="avatar-container">
                      {avatarSrc ? (
                        <img
                          src={avatarSrc}
                          alt={u.full_name || "User Avatar"}
                          className="avatar-img"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className="avatar-fallback"
                        style={{ display: avatarSrc ? "none" : "flex" }}
                      >
                        {initials}
                      </div>

                      {/* Google or Verified Indicator */}
                      {u.google_id ? (
                        <div
                          className="verification-dot bg-danger"
                          title="Connected via Google"
                        >
                          <FaGoogle style={{ fontSize: "0.6rem" }} />
                        </div>
                      ) : u.phone_verified === "1" || u.email_verified === "1" ? (
                        <div
                          className="verification-dot bg-success"
                          title="Verified User"
                        >
                          <FaCheckCircle style={{ fontSize: "0.6rem" }} />
                        </div>
                      ) : null}
                    </div>

                    {/* User Name */}
                    <h5 className="fw-bold text-dark mb-1 text-truncate" title={u.full_name || "Customer"}>
                      {u.full_name || "Customer"}
                    </h5>

                    {/* Location */}
                    <p className="text-muted small mb-3">
                      <FaMapMarkerAlt className="text-danger me-1" style={{ fontSize: "0.75rem" }} />
                      {u.city ? u.city : "Location Not Provided"}
                      {u.state ? `, ${u.state}` : ""}
                    </p>

                    {/* Contact Badges */}
                    <div className="d-flex flex-column gap-1.5 text-start bg-light p-2.5 rounded-3 mb-3" style={{ fontSize: "0.78rem" }}>
                      {/* Phone */}
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="text-muted d-flex align-items-center gap-1.5">
                          <FaPhoneAlt className="text-primary" style={{ fontSize: "0.7rem" }} />
                          {u.phone_number ? (
                            <a href={`tel:${u.phone_number}`} className="text-dark fw-semibold text-decoration-none">
                              {u.phone_number}
                            </a>
                          ) : (
                            <span className="text-muted fst-italic">No Phone</span>
                          )}
                        </span>
                        {u.phone_verified === "1" && (
                          <span className="badge bg-success bg-opacity-10 text-success p-1 px-1.5" style={{ fontSize: "0.65rem" }}>
                            Verified
                          </span>
                        )}
                      </div>

                      {/* Email */}
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="text-muted d-flex align-items-center gap-1.5 text-truncate" style={{ maxWidth: "80%" }}>
                          <FaEnvelope className="text-secondary" style={{ fontSize: "0.7rem" }} />
                          {u.email ? (
                            <a href={`mailto:${u.email}`} className="text-dark fw-semibold text-decoration-none text-truncate" title={u.email}>
                              {u.email}
                            </a>
                          ) : (
                            <span className="text-muted fst-italic">No Email</span>
                          )}
                        </span>
                        {u.email_verified === "1" && (
                          <span className="badge bg-info bg-opacity-10 text-info p-1 px-1.5" style={{ fontSize: "0.65rem" }}>
                            Verified
                          </span>
                        )}
                      </div>

                      {/* Gender & Age */}
                      {(u.gender || u.date_of_birth) && (
                        <div className="d-flex align-items-center justify-content-between pt-1 border-top mt-1 text-muted" style={{ fontSize: "0.72rem" }}>
                          <span>
                            {u.gender === "m" ? "Male" : u.gender === "f" ? "Female" : "Gender: N/A"}
                          </span>
                          {u.date_of_birth && (
                            <span>DOB: {u.date_of_birth}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Platform Engagement Badge */}
                    <div className="mt-auto pt-2">
                      <div className="d-flex justify-content-between align-items-center mb-2 px-1 text-muted small" style={{ fontSize: "0.74rem" }}>
                        <span>Total Clicks:</span>
                        <span className="fw-bold text-primary">{totalClicks} clicks</span>
                      </div>

                      <button
                        className="btn btn-outline-primary btn-sm w-100 rounded-pill d-flex align-items-center justify-content-center gap-1.5 py-1.5"
                        onClick={() => handleOpenDetailModal(u)}
                      >
                        <FaEye /> View Full Profile
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* =======================================
             TABLE VIEW
             ======================================= */
          <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 info-table">
                <thead className="table-light">
                  <tr style={{ fontSize: "0.8rem", textTransform: "uppercase" }}>
                    <th>User</th>
                    <th>Full Name</th>
                    <th>Mobile</th>
                    <th>Email</th>
                    <th>City / State</th>
                    <th>Gender / DOB</th>
                    <th>Activity</th>
                    <th>Registered</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const avatarSrc = getAvatarUrl(u.avatar);
                    const initials = getUserInitials(u);
                    const totalClicks =
                      (u.restaurant_clicks_count || 0) +
                      (u.product_clicks_count || 0) +
                      (u.recipe_clicks_count || 0) +
                      (u.video_clicks_count || 0);

                    return (
                      <tr key={u.id}>
                        {/* Avatar */}
                        <td style={{ width: 60 }}>
                          <div style={{ width: 44, height: 44, position: "relative" }}>
                            {avatarSrc ? (
                              <img
                                src={avatarSrc}
                                alt={u.full_name}
                                style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover" }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                                color: "#fff",
                                display: avatarSrc ? "none" : "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                                fontSize: "1rem",
                              }}
                            >
                              {initials}
                            </div>
                          </div>
                        </td>

                        {/* Name & ID */}
                        <td>
                          <div className="fw-bold text-dark">{u.full_name || "Customer"}</div>
                          <small className="text-muted">ID: #{u.id}</small>
                        </td>

                        {/* Mobile */}
                        <td>
                          {u.phone_number ? (
                            <div>
                              <a href={`tel:${u.phone_number}`} className="text-dark fw-semibold text-decoration-none">
                                {u.phone_number}
                              </a>
                              {u.phone_verified === "1" && (
                                <FaCheckCircle className="text-success ms-1" style={{ fontSize: "0.7rem" }} title="Verified Phone" />
                              )}
                            </div>
                          ) : (
                            <span className="text-muted fst-italic small">N/A</span>
                          )}
                        </td>

                        {/* Email */}
                        <td>
                          {u.email ? (
                            <div>
                              <a href={`mailto:${u.email}`} className="text-dark text-decoration-none small">
                                {u.email}
                              </a>
                              {u.email_verified === "1" && (
                                <FaCheckCircle className="text-info ms-1" style={{ fontSize: "0.7rem" }} title="Verified Email" />
                              )}
                            </div>
                          ) : (
                            <span className="text-muted fst-italic small">N/A</span>
                          )}
                        </td>

                        {/* City / State */}
                        <td>
                          <span className="text-dark small">
                            {u.city ? u.city : "—"}
                            {u.state ? `, ${u.state}` : ""}
                          </span>
                        </td>

                        {/* Gender / DOB */}
                        <td>
                          <span className="badge bg-light text-secondary border small">
                            {u.gender === "m" ? "Male" : u.gender === "f" ? "Female" : "N/A"}
                          </span>
                          {u.date_of_birth && (
                            <small className="text-muted d-block" style={{ fontSize: "0.7rem" }}>
                              {u.date_of_birth}
                            </small>
                          )}
                        </td>

                        {/* Total Clicks */}
                        <td>
                          <span className="badge bg-primary bg-opacity-10 text-primary px-2.5 py-1">
                            {totalClicks} clicks
                          </span>
                        </td>

                        {/* Registered */}
                        <td>
                          <small className="text-muted">{u.created_at || "N/A"}</small>
                        </td>

                        {/* Action */}
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-primary rounded-pill px-3"
                            onClick={() => handleOpenDetailModal(u)}
                          >
                            <FaEye className="me-1" /> View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* =======================================
         DETAILED USER PERSONAL INFO MODAL
         ======================================= */}
      <div
        className="modal fade"
        id="userPersonalDetailModal"
        tabIndex="-1"
        aria-labelledby="userPersonalDetailModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
            {/* Modal Header */}
            <div
              className="modal-header text-white px-4 py-3"
              style={{ background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)" }}
            >
              <div className="d-flex align-items-center gap-2">
                <FaIdCard className="fs-5" />
                <h5 className="modal-title fw-bold mb-0" id="userPersonalDetailModalLabel">
                  User Personal Profile Details
                </h5>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>

            {/* Modal Body */}
            {selectedUser && (
              <div className="modal-body p-4 bg-light">
                {/* Hero Profile Header */}
                <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 bg-white">
                  <div className="d-flex flex-wrap align-items-center gap-4">
                    {/* Big Avatar */}
                    <div style={{ width: 110, height: 110, position: "relative" }}>
                      {getAvatarUrl(selectedUser.avatar) ? (
                        <img
                          src={getAvatarUrl(selectedUser.avatar)}
                          alt={selectedUser.full_name}
                          style={{
                            width: 110,
                            height: 110,
                            borderRadius: "50%",
                            objectFit: "cover",
                            boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
                            border: "4px solid #f1f5f9",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 110,
                            height: 110,
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: "2.8rem",
                            boxShadow: "0 6px 18px rgba(99,102,241,0.3)",
                            border: "4px solid #f1f5f9",
                          }}
                        >
                          {getUserInitials(selectedUser)}
                        </div>
                      )}
                      {selectedUser.google_id && (
                        <span
                          className="position-absolute bottom-0 end-0 bg-danger text-white rounded-circle p-1.5 shadow"
                          style={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}
                          title="Google Profile"
                        >
                          <FaGoogle style={{ fontSize: "0.75rem" }} />
                        </span>
                      )}
                    </div>

                    {/* Basic Info */}
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                        <h4 className="fw-bold text-dark mb-0">{selectedUser.full_name || "Customer"}</h4>
                        <span className="badge bg-primary text-white rounded-pill px-2.5 py-1 text-capitalize">
                          {selectedUser.role || "User"}
                        </span>
                        <span className="badge bg-light text-secondary border">
                          User ID: #{selectedUser.id}
                        </span>
                      </div>

                      <p className="text-muted small mb-2">
                        <FaMapMarkerAlt className="text-danger me-1" />
                        {selectedUser.city || "City Not Provided"}
                        {selectedUser.state ? `, ${selectedUser.state}` : ""}
                        {selectedUser.country ? `, ${selectedUser.country}` : ""}
                      </p>

                      <div className="d-flex flex-wrap gap-2 pt-1">
                        {selectedUser.phone_number && (
                          <a
                            href={`tel:${selectedUser.phone_number}`}
                            className="btn btn-sm btn-outline-success rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1.5"
                          >
                            <FaPhoneAlt style={{ fontSize: "0.7rem" }} /> Call: {selectedUser.phone_number}
                          </a>
                        )}
                        {selectedUser.email && (
                          <a
                            href={`mailto:${selectedUser.email}`}
                            className="btn btn-sm btn-outline-primary rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1.5"
                          >
                            <FaEnvelope style={{ fontSize: "0.7rem" }} /> Mail: {selectedUser.email}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Information Sections Grid */}
                <div className="row g-3 mb-4">
                  {/* Personal & Demographics */}
                  <div className="col-12 col-md-6">
                    <div className="card border-0 shadow-sm rounded-4 p-3.5 h-100 bg-white">
                      <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom d-flex align-items-center gap-2">
                        <FaUser className="text-primary" /> Personal & Demographics
                      </h6>
                      <table className="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted small" style={{ width: "40%" }}>Full Name:</td>
                            <td className="fw-semibold text-dark small">{selectedUser.full_name || "N/A"}</td>
                          </tr>
                          <tr>
                            <td className="text-muted small">Gender:</td>
                            <td className="fw-semibold text-dark small text-capitalize">
                              {selectedUser.gender === "m"
                                ? "Male"
                                : selectedUser.gender === "f"
                                ? "Female"
                                : "Not Specified"}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted small">Date of Birth:</td>
                            <td className="fw-semibold text-dark small">{selectedUser.date_of_birth || "Not Provided"}</td>
                          </tr>
                          <tr>
                            <td className="text-muted small">Account Role:</td>
                            <td className="fw-semibold text-dark small text-capitalize">{selectedUser.role || "User"}</td>
                          </tr>
                          <tr>
                            <td className="text-muted small">Google Connected:</td>
                            <td className="small">
                              {selectedUser.google_id ? (
                                <span className="badge bg-success bg-opacity-10 text-success">
                                  Linked (ID: {selectedUser.google_id})
                                </span>
                              ) : (
                                <span className="badge bg-light text-muted border">No</span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted small">Registered At:</td>
                            <td className="fw-semibold text-dark small">{selectedUser.created_at || "N/A"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Contact & Location Details */}
                  <div className="col-12 col-md-6">
                    <div className="card border-0 shadow-sm rounded-4 p-3.5 h-100 bg-white">
                      <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom d-flex align-items-center gap-2">
                        <FaMapMarkerAlt className="text-danger" /> Contact & Location
                      </h6>
                      <table className="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted small" style={{ width: "40%" }}>Mobile Phone:</td>
                            <td className="fw-semibold text-dark small">
                              {selectedUser.phone_number || "Not Provided"}
                              {selectedUser.phone_verified === "1" ? (
                                <span className="badge bg-success ms-1" style={{ fontSize: "0.6rem" }}>Verified</span>
                              ) : (
                                <span className="badge bg-secondary ms-1" style={{ fontSize: "0.6rem" }}>Unverified</span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted small">Email Address:</td>
                            <td className="fw-semibold text-dark small">
                              {selectedUser.email || "Not Provided"}
                              {selectedUser.email_verified === "1" ? (
                                <span className="badge bg-success ms-1" style={{ fontSize: "0.6rem" }}>Verified</span>
                              ) : (
                                <span className="badge bg-secondary ms-1" style={{ fontSize: "0.6rem" }}>Unverified</span>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted small">Address:</td>
                            <td className="fw-semibold text-dark small">{selectedUser.address || "Not Provided"}</td>
                          </tr>
                          <tr>
                            <td className="text-muted small">City:</td>
                            <td className="fw-semibold text-dark small">{selectedUser.city || "Not Provided"}</td>
                          </tr>
                          <tr>
                            <td className="text-muted small">State:</td>
                            <td className="fw-semibold text-dark small">{selectedUser.state || "Not Provided"}</td>
                          </tr>
                          <tr>
                            <td className="text-muted small">Country:</td>
                            <td className="fw-semibold text-dark small">{selectedUser.country || "Not Provided"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Engagement & Analytics Activity Breakdown */}
                <div className="card border-0 shadow-sm rounded-4 p-3.5 bg-white">
                  <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom d-flex align-items-center gap-2">
                    <FaMousePointer className="text-purple" style={{ color: "#7c3aed" }} /> Platform Activity & Engagement
                  </h6>
                  <div className="row g-2 text-center">
                    <div className="col-4 col-md-2">
                      <div className="p-2.5 rounded-3 bg-light">
                        <div className="fw-bold text-primary fs-5">{selectedUser.restaurant_clicks_count || 0}</div>
                        <small className="text-muted" style={{ fontSize: "0.72rem" }}>Restaurant Clicks</small>
                      </div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="p-2.5 rounded-3 bg-light">
                        <div className="fw-bold text-success fs-5">{selectedUser.product_clicks_count || 0}</div>
                        <small className="text-muted" style={{ fontSize: "0.72rem" }}>Product Clicks</small>
                      </div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="p-2.5 rounded-3 bg-light">
                        <div className="fw-bold text-warning fs-5">{selectedUser.recipe_clicks_count || 0}</div>
                        <small className="text-muted" style={{ fontSize: "0.72rem" }}>Recipe Clicks</small>
                      </div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="p-2.5 rounded-3 bg-light">
                        <div className="fw-bold text-danger fs-5">{selectedUser.video_clicks_count || 0}</div>
                        <small className="text-muted" style={{ fontSize: "0.72rem" }}>Video Clicks</small>
                      </div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="p-2.5 rounded-3 bg-light">
                        <div className="fw-bold text-info fs-5">{selectedUser.nearby_searches_count || 0}</div>
                        <small className="text-muted" style={{ fontSize: "0.72rem" }}>Nearby Searches</small>
                      </div>
                    </div>
                    <div className="col-4 col-md-2">
                      <div className="p-2.5 rounded-3 bg-light">
                        <div className="fw-bold text-purple fs-5" style={{ color: "#7c3aed" }}>{selectedUser.action_clicks_count || 0}</div>
                        <small className="text-muted" style={{ fontSize: "0.72rem" }}>Action Clicks</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal Footer */}
            <div className="modal-footer bg-white border-top px-4 py-3">
              <button
                type="button"
                className="btn btn-light fw-bold px-4"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPersonalInfo;
